<template>
  <aside class="bg-slate-800 text-white w-80 p-4 overflow-y-auto">
    <!-- Project Info -->
    <div class="mb-6">
      <h2 class="text-lg font-semibold text-white mb-1">{{ project.name }}</h2>
      <p class="text-gray-300 text-sm">{{ pages.length }} pages</p>
    </div>

    <!-- Page Tree -->
    <div class="space-y-2">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-medium text-gray-300 uppercase tracking-wide">Pages</h3>
        <Button
          icon="pi pi-plus"
          size="small"
          text
          class="text-gray-300 hover:text-white"
          @click="$emit('create-page')"
        />
      </div>

      <div class="space-y-1">
        <div
          v-for="page in pages"
          :key="page.id"
          class="group flex items-center space-x-2 p-2 rounded hover:bg-gray-700 cursor-pointer"
          :class="{
            'bg-gray-700': currentPage?.id === page.id
          }"
          @click="$emit('select-page', page)"
          @contextmenu.prevent="showPageMenu(page, $event)"
        >
          <!-- Page Icon -->
          <i class="pi text-gray-400 text-sm" :class="getPageIcon(page.layoutType)"></i>

          <!-- Page Title -->
          <span class="flex-1 text-sm text-white truncate">{{ page.title }}</span>

          <!-- Status Icon -->
          <i
            :class="getStatusIcon(page.status ?? 'draft')"
            :title="getStatusTitle(page.status ?? 'draft')"
            class="text-sm"
          ></i>

          <!-- Actions Menu Button -->
          <Button
            icon="pi pi-ellipsis-v"
            size="small"
            text
            class="text-gray-400 hover:text-white opacity-0 group-hover:opacity-100"
            @click.stop="showPageMenu(page, $event)"
          />
        </div>
      </div>
    </div>

    <!-- Page Context Menu -->
    <Menu ref="pageMenuRef" :model="pageMenuItems" :popup="true" />

    <!-- Menus Link -->
    <div class="mt-6 pt-6 border-t border-gray-700">
      <router-link
        :to="{ name: 'menu-editor', params: { projectId: project.id } }"
        class="flex items-center space-x-2 p-2 rounded hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
      >
        <i class="pi pi-bars text-sm"></i>
        <span class="text-sm">Navigation Menu</span>
      </router-link>
    </div>

    <!-- Empty State -->
    <div v-if="pages.length === 0" class="text-center py-8">
      <i class="pi pi-file-o text-gray-400 text-3xl mb-3"></i>
      <p class="text-gray-300 text-sm mb-4">No pages yet</p>
      <Button 
        label="Create First Page"
        icon="pi pi-plus"
        size="small"
        @click="$emit('create-page')"
      />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'

interface Page {
  id: number
  title: string
  slug: string
  layoutType: string
  status?: 'draft' | 'published' | 'archived'
}

interface Props {
  project: any
  pages: Page[]
  currentPage?: Page
}

defineProps<Props>()

const emit = defineEmits<{
  'select-page': [page: Page]
  'create-page': [parentId?: number]
  'delete-page': [pageId: number]
}>()

// Context menu refs
const pageMenuRef = ref()
const selectedPage = ref<Page | null>(null)

// Context menu items
const pageMenuItems = computed<MenuItem[]>(() => [
  {
    label: 'Open Page',
    icon: 'pi pi-external-link',
    command: () => {
      if (selectedPage.value) {
        emit('select-page', selectedPage.value)
      }
    }
  },
  {
    separator: true
  },
  {
    label: 'Create Child Page',
    icon: 'pi pi-plus',
    command: () => {
      if (selectedPage.value) {
        emit('create-page', selectedPage.value.id)
      }
    }
  },
  {
    separator: true
  },
  {
    label: 'Delete Page',
    icon: 'pi pi-trash',
    class: 'text-red-600',
    command: () => {
      if (selectedPage.value) {
        emit('delete-page', selectedPage.value.id)
      }
    }
  }
])

const getPageIcon = (layoutType: string) => {
  const icons: Record<string, string> = {
    'default': 'pi-file',
    'landing': 'pi-home',
    'minimal': 'pi-file-o',
    'special': 'pi-star'
  }
  return icons[layoutType] || 'pi-file'
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    'draft': 'bg-orange-400',
    'published': 'bg-green-400',
    'archived': 'bg-gray-400'
  }
  return colors[status] || 'bg-gray-400'
}

const getStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    'published': 'pi pi-check-circle text-green-400',
    'draft': 'pi pi-clock text-orange-400',
    'archived': 'pi pi-archive text-gray-400'
  }
  return icons[status] || 'pi pi-circle text-gray-400'
}

const getStatusTitle = (status: string) => {
  const titles: Record<string, string> = {
    'published': 'Published - Live on website',
    'draft': 'Draft - Not published yet',
    'archived': 'Archived - No longer active'
  }
  return titles[status] || 'Unknown status'
}

const showPageMenu = (page: Page, event: Event) => {
  selectedPage.value = page
  pageMenuRef.value?.toggle(event)
}
</script>